/**
 * main_ui.js — Ventana principal y enrutador modular.
 *
 * Equivalente React de la ventana PyQt central:
 * - Importa MODULO_VISOR, MODULO_LABORAL, MODULO_TRAMITES y MODULO_DISENADOR sin acoplar su lógica interna.
 * - Gestiona el Panel General (tarjetas inferiores) vía eventos y localStorage.
 * - Al cambiar de tarjeta, el sidebar (Layout.jsx) muestra ÚNICAMENTE las pestañas del módulo activo.
 * - Valida permisos de módulos vía GET /system/info → entitlements (useEntitlements).
 *
 * Conexión de archivos:
 *   main_ui.js  ──importa──►  modulo_visor.js | modulo_laboral.js | modulo_tramites.js | modulo_disenador.js
 *   Layout.jsx  ──importa──►  main_ui.js (nav, guards, permisos)
 *   App.js      ──importa──►  main_ui.js (rutas fusionadas)
 *   workModule.js re-exporta main_ui para compatibilidad con imports legacy.
 */
import { Route, Navigate } from "react-router-dom";
import RegistroInicial from "@/pages/RegistroInicial";
import RecuperacionAcceso from "@/pages/RecuperacionAcceso";
import ChatInternoGeneral from "@/pages/equipo/ChatInternoGeneral";
import { EQUIPO_CHAT_PATH, EQUIPO_USUARIOS_PATH } from "@/modules/equipo/equipoRoutes";
import { PANEL_GENERAL_EQUIPO_TAB } from "@/modules/panelGeneralTabs";
import { MODULO_VISOR } from "@/modules/modulo_visor";
import { MODULO_LABORAL } from "@/modules/modulo_laboral";
import { MODULO_TRAMITES, isTramitesCrossModulePath } from "@/modules/modulo_tramites";
import { MODULO_DESPACHO, isDespachoCrossModulePath } from "@/modules/modulo_despacho";
import { MODULO_MATERIALIDAD, isMaterialidadCrossModulePath } from "@/modules/modulo_materialidad";
import { MODULO_ANALISIS_FINANCIERO, isAnalisisFinancieroPath } from "@/modules/modulo_analisis_financiero";
import { MODULO_DISENADOR } from "@/modules/modulo_disenador";
import { isEquipoBasePath } from "@/modules/equipo/equipoRoutes";
import {
  WORK_MODULES as CORE_WORK_MODULES,
  moduleContextLabel as coreModuleContextLabel,
  defaultRouteForModule as coreDefaultRouteForModule,
} from "@/modules/workModuleCore";

import { LABORAL_LEGACY_REDIRECT_PATHS, LABORAL_DELEGATION_PATHS } from "@/modules/laboralNavPolicy";
import { isDatosMaestrosAutonomousPath } from "@/modules/datosMaestros/datosMaestrosNavigation";
import { getPlatformRouteElements } from "@/modules/modulo_platform";

export { useClientModulePermissions } from "@/modules/useClientModulePermissions";
export { useEntitlements, invalidateEntitlementsCache } from "@/modules/useEntitlements";
export {
  getStoredWorkModule,
  setStoredWorkModule,
  isDesignerUser,
  getPanelCardsForUser,
  getModuleEntryPath,
  PREMIUM_LABOR_LOCKED_MESSAGE,
} from "@/modules/workModuleCore";

/** Registro ordenado de módulos disponibles en el Panel General. */
export const MODULE_REGISTRY = [
  MODULO_VISOR,
  MODULO_LABORAL,
  MODULO_TRAMITES,
  MODULO_DESPACHO,
  MODULO_MATERIALIDAD,
  MODULO_ANALISIS_FINANCIERO,
  MODULO_DISENADOR,
];

/** Mapa id → módulo para resolución O(1). */
export const MODULE_BY_ID = Object.fromEntries(MODULE_REGISTRY.map((m) => [m.id, m]));

/** Identificadores públicos (compatibles con workModule.js anterior). */
export const WORK_MODULES = CORE_WORK_MODULES;

export const MODULE_CARDS = Object.fromEntries(MODULE_REGISTRY.map((m) => [m.id, m.card]));

/**
 * Resuelve el descriptor del módulo por id.
 * @param {string} moduleId
 * @returns {typeof MODULO_VISOR | undefined}
 */
export function getModuleById(moduleId) {
  return MODULE_BY_ID[moduleId] || MODULE_BY_ID[MODULO_VISOR.id];
}

/** Pestañas del sidebar para el módulo activo (limpia menú anterior en Layout). */
export function navForWorkModule(moduleId) {
  const mod = getModuleById(moduleId);
  try {
    return mod.getNavItems();
  } catch {
    return MODULO_VISOR.getNavItems();
  }
}

export function pathsForWorkModule(moduleId) {
  return navForWorkModule(moduleId).map((item) => item.to);
}

export function defaultRouteForModule(moduleId) {
  const mod = getModuleById(moduleId);
  try {
    return mod.getDefaultRoute();
  } catch {
    return coreDefaultRouteForModule(moduleId);
  }
}

export function isPathAllowedForModule(pathname, moduleId, search = "", state = null) {
  const path = String(pathname || "").split("?")[0];
  if (isEquipoBasePath(path)) return true;
  if (LABORAL_LEGACY_REDIRECT_PATHS.has(path)) return true;
  if (LABORAL_DELEGATION_PATHS.has(path)) return true;
  if (isDatosMaestrosAutonomousPath(pathname, search, state)) return true;
  const mod = getModuleById(moduleId);
  try {
    if (mod.isPathAllowed(pathname)) return true;
    // Rutas legacy / cross-módulo hacia Trámites (redirect interno antes del guard).
    if (isTramitesCrossModulePath(path)) return true;
    if (isDespachoCrossModulePath(path)) return true;
    if (isMaterialidadCrossModulePath(path)) return true;
    if (isAnalisisFinancieroPath(path)) return true;
    return false;
  } catch {
    return (
      MODULO_VISOR.isPathAllowed(pathname)
      || isTramitesCrossModulePath(path)
      || isDespachoCrossModulePath(path)
      || isMaterialidadCrossModulePath(path)
      || isAnalisisFinancieroPath(path)
    );
  }
}

export function moduleContextLabel(moduleId) {
  const mod = getModuleById(moduleId);
  try {
    return mod.getContextLabel();
  } catch {
    return coreModuleContextLabel(moduleId);
  }
}

/**
 * Fusiona rutas de todos los módulos eliminando duplicados por path.
 * Rutas compartidas (/clientes, /ayuda, /) se registran una sola vez.
 */
function mergeModuleRoutes(routeLists) {
  const seen = new Set();
  const merged = [];
  for (const routes of routeLists) {
    for (const route of routes) {
      const key = route.index ? "__index__" : route.path;
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push(route);
    }
  }
  return merged;
}

/** Rutas de sistema (fuera de módulos de negocio). */
function getSystemRoutes() {
  return [
    { path: "registro-inicial", element: <RegistroInicial /> },
    { path: "recuperacion-acceso", element: <RecuperacionAcceso /> },
    { path: EQUIPO_USUARIOS_PATH.replace(/^\//, ""), element: <Navigate to={PANEL_GENERAL_EQUIPO_TAB} replace /> },
    { path: EQUIPO_CHAT_PATH.replace(/^\//, ""), element: <ChatInternoGeneral /> },
  ];
}

/**
 * Genera elementos <Route /> para App.js.
 * Cada módulo aporta sus rutas; main_ui las fusiona sin duplicar paths.
 */
export function buildProtectedRouteChildren() {
  let routeDefs;
  try {
    routeDefs = mergeModuleRoutes([
      MODULO_MATERIALIDAD.getRoutes(),
      MODULO_ANALISIS_FINANCIERO.getRoutes(),
      MODULO_TRAMITES.getRoutes(),
      MODULO_DESPACHO.getRoutes(),
      MODULO_VISOR.getRoutes(),
      MODULO_LABORAL.getRoutes(),
      MODULO_DISENADOR.getRoutes(),
      getSystemRoutes(),
    ]);
  } catch (err) {
    console.error("[main_ui] Error al fusionar rutas de módulos:", err);
    routeDefs = MODULO_VISOR.getRoutes();
  }

  const moduleRoutes = routeDefs.map((def) => {
    if (def.index) {
      return <Route key="__index__" index element={def.element} />;
    }
    return <Route key={def.path} path={def.path} element={def.element} />;
  });

  return [...moduleRoutes, getPlatformRouteElements()];
}

/** Alias legacy — equivalente a cargarModuloVisor() del diseño anterior. */
export function cargarModuloVisor() {
  return MODULO_VISOR.getNavItems();
}

export function cargarModuloLaboral() {
  return MODULO_LABORAL.getNavItems();
}

export function cargarModuloDisenador() {
  return MODULO_DISENADOR.getNavItems();
}

export function cargarModuloTramites() {
  return MODULO_TRAMITES.getNavItems();
}

export function cargarModuloDespacho() {
  return MODULO_DESPACHO.getNavItems();
}

export function cargarModuloMaterialidad() {
  return MODULO_MATERIALIDAD.getNavItems();
}

export function cargarModuloAnalisisFinanciero() {
  return MODULO_ANALISIS_FINANCIERO.getNavItems();
}
